import { Injectable, signal, WritableSignal } from '@angular/core';
import { HeaderinforesponseDto } from '../models/dto/headerinforesponse-dto';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../environments';
import { keyinforesponseDto } from '../models/dto/keyinforesponse-dto';
import { PerfRiskInfoResponseDto } from '../models/dto/perfriskinforesponse-dto';
import { HistoricalChartResponseDto } from '../models/dto/historicalchartresponse-dto';
import { map, Observable, BehaviorSubject, of, tap } from 'rxjs';
import { CompositionResponseDtoDto } from '../models/dto/compositionresponse-dto';
import { SubproductDto } from '../models/dto/subproduct-dto';
import { CumulativePerformanceResponse } from '../models/dto/cumulativeperformanceresponse-dto';
import { DocumentsDTO } from '../models/dto/document-dto';
import { saveAs } from 'file-saver';
import { NewsAndNoticesDTO } from '../models/dto/news-and-notices-dto';
import { MetaInfoService, MetaName } from '@sdpp-web/lib/portal';
import { Client, StompSubscription } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { IndexFooterDocumentResponseDto } from '../models/dto/indexfooterdocumentresponsedto-dto';
import { ProductDetailsDTO } from '../models/dto/product-details-dto';

const BASE_URL: string = `${environment.baseIndxApi}`;

@Injectable({ providedIn: 'root' })
export class IndexIoService {
  private indexHeaderInfoSignal = signal<HeaderinforesponseDto | null>(null);
  private indexSubProductInfoSignal = signal<SubproductDto | null>(null);
  private indexKeyInfoSignal = signal<keyinforesponseDto | null>(null);
  private indexCompositionInfoSignal = signal<
    CompositionResponseDtoDto[] | null
  >(null);
  private indexPerfRiskSignal: WritableSignal<
    PerfRiskInfoResponseDto[] | null
  > = signal<PerfRiskInfoResponseDto[] | null>(null);
  private indexSearchSignal: WritableSignal<ProductDetailsDTO[] | null> =
    signal<ProductDetailsDTO[] | null>(null);
  private indexDocumentsignal: WritableSignal<IndexFooterDocumentResponseDto | null> =
    signal<IndexFooterDocumentResponseDto | null>(null);
  private indexPerformanceTableSignal: WritableSignal<CumulativePerformanceResponse | null> =
    signal<CumulativePerformanceResponse | null>(null);
  private genericDocumentsignal: WritableSignal<DocumentsDTO[] | null> = signal<
    DocumentsDTO[] | null
  >(null);
  private documentsForHandBookAndIOSCOSignal: WritableSignal<
    DocumentsDTO[] | null
  > = signal<DocumentsDTO[] | null>(null);
  private documentsForComplaintsSignal: WritableSignal<DocumentsDTO[] | null> =
    signal<DocumentsDTO[] | null>(null);
  private newsAndNoticesSignal: WritableSignal<NewsAndNoticesDTO[] | null> =
    signal<NewsAndNoticesDTO[] | null>(null);
  public deployUrlHost = signal<string>('');
  private client: Client;
  private subscriptions = new Map<string, StompSubscription>();
  private priceSubjects = new Map<string, BehaviorSubject<string>>();
  private productIdMdsCodeMap = new Map<string, ProductDetailsDTO>();

  constructor(
    private http: HttpClient,
    private readonly _metaInfoService: MetaInfoService
  ) {
    const deployUrlAppValue = this._metaInfoService.getMeta(MetaName.DeployUrl);
    this.client = new Client({
      webSocketFactory: () => new SockJS(BASE_URL + '/index/ws-price'),
    });
    this.client.reconnectDelay = 300000; // auto‑reconnect on network loss
    this.client.activate();
    console.log('deploy url host value ' + deployUrlAppValue);
    if (typeof deployUrlAppValue === 'string') {
      this.deployUrlHost.set(deployUrlAppValue);
    }
  }

  get getDeployUrlHost() {
    return this.deployUrlHost.asReadonly();
  }

  public getIndexHeaderInfo(mdsCode: string) {
    this.http
      .get<HeaderinforesponseDto>(`${BASE_URL}/index/headerinfo/${mdsCode}`)
      .subscribe((data) => this.indexHeaderInfoSignal.set(data));
  }

  public getSubProductInfo(mdsCode: string) {
    this.http
      .get<SubproductDto>(`${BASE_URL}/index/subproduct/${mdsCode}`)
      .subscribe((data) => this.indexSubProductInfoSignal.set(data));
  }

  get indexHeaderInfo() {
    return this.indexHeaderInfoSignal.asReadonly();
  }
  get indexSubProductInfo() {
    return this.indexSubProductInfoSignal.asReadonly();
  }

  public getIndexKeyInfo(mdsCode: string) {
    this.http
      .get<keyinforesponseDto>(`${BASE_URL}/index/keyinfo/${mdsCode}`)
      .subscribe((data) => this.indexKeyInfoSignal.set(data));
  }

  public getCompositionInfo(mdsCode: string) {
    this.http
      .get<CompositionResponseDtoDto[]>(
        `${BASE_URL}/index/composition/${mdsCode}`
      )
      .subscribe((data) => this.indexCompositionInfoSignal.set(data));
  }

  public getIndexHistoricalchartInfo(
    mdsCode: string,
    startDate: string,
    endDate: string
  ): Observable<HistoricalChartResponseDto[]> {
    const params = { mdsCode, startDate, endDate };
    return this.http.get<HistoricalChartResponseDto[]>(
      `${BASE_URL}/index/getPerformanceGraphByPeriod`,
      { params }
    );
  }

  public getProductDetailsByBloombergCode(bloombergCode: string): Observable<ProductDetailsDTO> {
    // const dummyDetails: ProductDetailsDTO = {
    //   mdsCode: 'EU_AMC_ENHAGBFI',
    //   productId: 2345,
    //   subProductId: 567,
    //   bloombergCode: 'ENHAGBFI Index',
    //   technicalName: ''
    // }
    // return of(dummyDetails);
    return this.http.get<ProductDetailsDTO>(`${BASE_URL}/index/getProductDataByBBGCode?bbgCode=${bloombergCode}`);

  }

  public saveProudctIdAndMdsCode(productDetails: ProductDetailsDTO) {
    this.productIdMdsCodeMap.set(`${productDetails.SubProductId}`, productDetails);
  }

  public getProductDetailsFromSubProductId(subProductId: string) {
    const safeId = encodeURIComponent(subProductId.trim());
    const details = this.productIdMdsCodeMap.get(`${subProductId}`);
    if (!details) {
      return this.http.get<ProductDetailsDTO>(`${BASE_URL}/index/getProductDataByProductId?subProductId=${safeId}`).pipe(
        tap((details) => {
          this.productIdMdsCodeMap.set(`${details.SubProductId}`, details)
        })
      );
    }
    return of(details);
  }

  get indexKeyInfo() {
    return this.indexKeyInfoSignal.asReadonly();
  }

  get indexCompositionInfo() {
    return this.indexCompositionInfoSignal.asReadonly();
  }

  public getPerfRiskInfo(mdsCode: string) {
    this.http
      .get<PerfRiskInfoResponseDto[]>(
        `${BASE_URL}/index/getPerformanceData/${mdsCode}`
      )
      .subscribe((data) => this.indexPerfRiskSignal.set(data));
  }

  get indexPerfRiskInfo() {
    return this.indexPerfRiskSignal.asReadonly();
  }

  public getSearchResults(value: string) {
    this.http
      .get<ProductDetailsDTO[]>(`${BASE_URL}/index/search/${value}`)
      .subscribe((data) => {
        this.indexSearchSignal.set(data);
        data.forEach((product) => {
          this.productIdMdsCodeMap.set(`${product.SubProductId}`, product);
        })
      });
  }

  get indexSearchInfo() {
    return this.indexSearchSignal.asReadonly();
  }

  public getIndexDocuments(mdsCode: string) {
    this.http
      .get<IndexFooterDocumentResponseDto>(
        `${BASE_URL}/index/document/indexDocuments/${mdsCode}`
      )
      .subscribe((data) => this.indexDocumentsignal.set(data));
  }

  get indexDocumentsInfo() {
    return this.indexDocumentsignal.asReadonly();
  }

  public getPerformanceTableResults(countryCode: string) {
    this.http
      .get<CumulativePerformanceResponse>(
        `${BASE_URL}/index/getCumulativeCalendarPerformance/${countryCode}`
      )
      .subscribe((data) => this.indexPerformanceTableSignal.set(data));
  }

  get indexPerformanceTable() {
    return this.indexPerformanceTableSignal.asReadonly();
  }

  public downloadHistoricalPerformanceExcel(
    mdsCode: string,
    countryCode: string
  ): Observable<void> {
    return this.http
      .get(
        `${BASE_URL}/index/performance-index-file/${mdsCode}/${countryCode}`,
        {
          responseType: 'arraybuffer',
        }
      )
      .pipe(
        map(
          (arraybuffer: ArrayBuffer) => new Blob([new Uint8Array(arraybuffer)])
        ),
        map((blob: Blob) => {
          saveAs(blob, mdsCode + '.xlsx');
        })
      );
  }
  public getGenericDocuments() {
    this.http
      .get<DocumentsDTO[]>(`${BASE_URL}/index/document/genericDocuments`)
      .subscribe((data) => this.genericDocumentsignal.set(data));
  }

  get genericDocumentsInfo() {
    return this.genericDocumentsignal.asReadonly();
  }

  public getDocumentsForHandBookAndIOSCO() {
    this.http
      .get<DocumentsDTO[]>(`${BASE_URL}/index/document/handBookIoscoDocuments`)
      .subscribe((data) => this.documentsForHandBookAndIOSCOSignal.set(data));
  }

  get documentsForHandBookAndIOSCOInfo() {
    return this.documentsForHandBookAndIOSCOSignal.asReadonly();
  }

  public getDocumentsForComplaints() {
    this.http
      .get<DocumentsDTO[]>(`${BASE_URL}/index/document/complaintDocuments`)
      .subscribe((data) => this.documentsForComplaintsSignal.set(data));
  }

  get documentsForComplaintsInfo() {
    return this.documentsForComplaintsSignal.asReadonly();
  }

  public getNewsAndNotices(countryCode: string) {
    let params = new HttpParams();
    if (countryCode) {
      params = params.set('countryCode', countryCode);
    }
    this.http
      .get<NewsAndNoticesDTO[]>(`${BASE_URL}/index/news-and-notices/all`, {
        params,
      })
      .subscribe((data) => this.newsAndNoticesSignal.set(data));
  }

  get newsAndNoticesInfo() {
    return this.newsAndNoticesSignal.asReadonly();
  }

  public downloadDocument(
    documentType: string,
    documentId: string,
    title: string,
    fileExtension: string,
    s3Key: string
  ): Observable<void> {
    // Build the query‑string exactly as the Spring controller expects
    let params = new HttpParams()
      .set('s3Key', s3Key)
      .set('documentId', documentId);

    if (title) {
      params = params.set('title', title);
    }

    // The backend returns raw bytes → we ask for a Blob
    return this.http
      .get(`${BASE_URL}/index/document/file`, {
        params,
        responseType: 'arraybuffer', // <-- important, otherwise Angular will try to JSON‑parse
      })
      .pipe(
        map(
          (arraybuffer: ArrayBuffer) => new Blob([new Uint8Array(arraybuffer)])
        ),
        map((blob: Blob) => {
          const fileName =
            documentType === 'News' ? title : `${title}.${fileExtension}`;
          saveAs(blob, fileName);
        })
      );
  }

  subscribeTREPService(mdsCode: string): Observable<string> {
    if (!this.priceSubjects.has(mdsCode)) {
      this.priceSubjects.set(mdsCode, new BehaviorSubject<string>(''));
    }

    // send a “subscribe” control message to the backend
    this.publishMessage(mdsCode, 'subscribe');

    if (this.client.connected) {
      const sub = this.client.subscribe(
        `/topic/live-price/${mdsCode}`,
        (msg) => {
          const price = msg.body;
          this.priceSubjects.get(mdsCode)!.next(price);
        }
      );
      this.subscriptions.set(mdsCode, sub);
    }
    return this.priceSubjects.get(mdsCode)!.asObservable();
  }

  publishMessage(mdsCode: string, action: string) {
    if (this.client && this.client.connected) {
      this.client.publish({
        destination: `/api/index/trep/${action}/${mdsCode}`,
      });
    } else {
      console.warn('STOMP client is not connected. Cannot unsubscribe.');
      // Optionally retry or handle the error
    }
  }

  unsubscribeTREPService(mdsCode: string) {
    const sub = this.subscriptions.get(mdsCode);
    if (sub) {
      sub.unsubscribe();
      this.subscriptions.delete(mdsCode);
    }
    this.publishMessage(mdsCode, 'unsubscribe');
    this.priceSubjects.delete(mdsCode);
  }
}
